import { useLayoutEffect, useState, type RefObject } from 'react';
import { flushSync } from 'react-dom';

/**
 * The building's width at 1×: as wide as it can be with its whole height in the
 * stage. Its real width, not a CSS scale, since the chips measure the building in
 * screen pixels. The height follows the width (the SVG's viewBox), so its ratio
 * holds at any width. Undefined until the stage and the building have a size.
 *
 * Fitted before paint, on mount and on every resize: otherwise entering
 * fullscreen paints one frame of the building at the stage's full width.
 */
export function useBuildingFit(
  stageRef: RefObject<HTMLElement | null>,
  buildingRef: RefObject<HTMLElement | null>,
): number | undefined {
  const [fit, setFit] = useState<number>();

  useLayoutEffect(() => {
    const stage = stageRef.current;
    const building = buildingRef.current;
    if (!stage || !building) return;
    const measure = () => {
      // Fractional, not `offsetWidth`/`offsetHeight`: whole pixels round the
      // ratio differently at each width, so the fit's own resize would re-fit
      // it a pixel off, again and again, and the chips would shake (zoomed in,
      // the error is multiplied).
      const { width, height } = building.getBoundingClientRect();
      if (width === 0 || height === 0) return;
      setFit(Math.min(stage.clientWidth, (stage.clientHeight * width) / height));
    };
    measure();
    const observer = new ResizeObserver(() => flushSync(measure));
    observer.observe(stage);
    observer.observe(building);
    return () => observer.disconnect();
  }, [stageRef, buildingRef]);

  return fit;
}
